import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Phone } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { INVESTOR_ARTICLES } from "@/data/investor-articles";

const fade = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };

export default function InvestorArticlePage() {
  const { slug } = useParams();
  const article = INVESTOR_ARTICLES.find(a => a.slug === slug);
  const others = INVESTOR_ARTICLES.filter(a => a.slug !== slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!article) return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center gap-6 px-6">
      <p className="text-xl tracking-tight">Article not found</p>
      <Link to="/for-investors" className="text-zinc-400 hover:text-white text-xs tracking-wider">Back to Investors</Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-black text-white">
      <article className="pt-32 pb-20 px-6 md:px-16 lg:px-28 max-w-4xl mx-auto">
        <Link to="/for-investors" className="inline-flex items-center gap-2 text-[10px] text-zinc-500 hover:text-white transition-colors mb-10 tracking-[0.2em]">
          <ArrowLeft className="w-3 h-3" /> For Investors
        </Link>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-2xl sm:text-3xl md:text-4xl tracking-tight leading-[1.12]">{article.title}</h1>
          <p className="text-zinc-500 text-sm mt-8 tracking-wider leading-relaxed">{article.summary}</p>
        </motion.div>

        {/* Article body */}
        <div className="mt-16 space-y-8">
          {article.content.map((para, i) => (
            <motion.p key={i} variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
              className="text-zinc-400 text-sm tracking-wider leading-[1.8]">{para}</motion.p>
          ))}
        </div>

        {/* FAQs */}
        {article.faqs && article.faqs.length > 0 && (
          <div className="mt-20 pt-12 border-t border-white/10">
            <h2 className="text-xl tracking-tight mb-8">Questions on this topic.</h2>
            <Accordion type="single" collapsible>
              {article.faqs.map((f, i) => (
                <AccordionItem key={i} value={`af-${i}`} className="border-white/10">
                  <AccordionTrigger className="text-left text-white hover:no-underline text-xs tracking-wider py-5">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-zinc-400 text-[10px] leading-relaxed tracking-wider pb-5">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        )}

        {/* CTA */}
        <div className="mt-20 pt-12 border-t border-white/10">
          <h3 className="text-lg tracking-tight">Need help with this for your company?</h3>
          <p className="text-zinc-500 text-xs mt-4 tracking-wider leading-relaxed">We work with funded startups and portfolio companies to build the perception and growth systems that make investments pay off. Talk to us — no pitch, just a real conversation about where you are and what might help.</p>
          <div className="flex flex-wrap gap-4 mt-8">
            <a href="https://wa.me/919041344420?text=Hi%20Prospera%2C%20I%20read%20your%20article%20on%20this%20topic%20and%20I%20need%20help%20with%20something%20similar." target="_blank" rel="noopener noreferrer"
              className="bg-white text-black px-8 py-4 text-xs tracking-[0.2em] hover:bg-zinc-200 transition-colors inline-flex items-center gap-3">
              Talk to us <ArrowRight className="w-4 h-4" />
            </a>
            <a href="tel:+916239547599" className="text-zinc-500 hover:text-white text-xs tracking-wider transition-colors inline-flex items-center gap-2 px-4">
              <Phone className="w-3 h-3" /> +91 6239 547 599
            </a>
          </div>
        </div>

        {/* Related articles */}
        <div className="mt-20 pt-12 border-t border-white/10">
          <h3 className="text-lg tracking-tight mb-8">Keep reading.</h3>
          <div className="space-y-4">
            {others.slice(0, 4).map(a => (
              <Link key={a.slug} to={`/for-investors/${a.slug}`}
                className="block border border-white/10 p-6 hover:border-white/30 transition-colors group">
                <p className="text-sm tracking-wider transition-colors">{a.title}</p>
                <p className="text-zinc-600 text-[10px] mt-2 tracking-wider">{a.summary.slice(0, 120)}...</p>
              </Link>
            ))}
          </div>
        </div>

        {/* SEO keywords */}
        <div className="mt-12 flex flex-wrap gap-2">
          {article.keywords.map(kw => <span key={kw} className="text-[9px] text-zinc-800 border border-white/5 px-2 py-1 tracking-wider">{kw}</span>)}
        </div>
      </article>
    </div>
  );
}
